import { useEffect, useRef, useState } from 'react';
import { DESKTOP_THEME as DT } from '../../theme/desktopTokens';
import type { ChatMessage } from '../../types/game';
import { CountryFlag } from '../ui/CountryFlag';
import { useTranslation } from '../../i18n/react';

interface DesktopChatSectionProps {
  messages: ChatMessage[];
  sendMessage: (body: string) => void;
  /** When true, hide the message list and show only the input row (collapsed
   *  chat, per the GAMESCENE "main" state). Default false = list + input. */
  collapsed?: boolean;
}

const MAX_BODY = 200;

export function DesktopChatSection({ messages, sendMessage, collapsed = false }: DesktopChatSectionProps) {
  const { t } = useTranslation('gameplay');
  const [input, setInput] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom on new messages if already near bottom
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    if (isNearBottom) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  function handleSend() {
    const body = input.trim();
    if (!body) return;
    sendMessage(body);
    setInput('');
    inputRef.current?.focus();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  const canSend = input.trim().length > 0;

  return (
    // flex:1 + minHeight:0 lets the parent (GameScreen chat wrapper) control the height.
    // Message list uses flex:1 instead of height:200 so it grows/shrinks with the panel.
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, flex: collapsed ? undefined : 1, minHeight: 0 }}>
      {/* Message list — flexible height, min 80px so it never collapses.
          Hidden when collapsed (chat "main" state shows only the input). */}
      {!collapsed && (
      <div
        ref={listRef}
        style={{
          background:    DT.color.panelFlat,
          border:        `1px solid ${DT.color.border}`,
          borderRadius:  12,
          flex:          1,
          minHeight:     80,
          overflowY:     'auto',
          padding:       '10px 12px',
          display:       'flex',
          flexDirection: 'column',
          gap:           8,
        }}
      >
        {messages.length === 0 ? (
          <div
            style={{
              flex:           1,
              display:        'flex',
              flexDirection:  'column',
              alignItems:     'center',
              justifyContent: 'center',
              gap:            6,
            }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ opacity: 0.15 }}>
              <path
                d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"
                stroke={DT.color.textSecondary}
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
            </svg>
            <span style={{ fontSize: 11, color: DT.color.textMuted, fontFamily: 'Montserrat, sans-serif' }}>
              {t('chat.empty')}
            </span>
          </div>
        ) : (
          messages.map(msg => <DesktopMessageRow key={msg.id} msg={msg} />)
        )}
      </div>
      )}

      {/* Input row — Figma: white pill, gold border, dark text + dark send icon. */}
      <div
        style={{
          display:      'flex',
          alignItems:   'center',
          background:   '#FFFFFF',
          border:       `1px solid ${DT.color.gold}`,
          borderRadius: 16,
          overflow:     'hidden',
          marginTop:    collapsed ? 0 : 10,
        }}
      >
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          maxLength={MAX_BODY}
          placeholder={t('chat.placeholder')}
          className="font-sans"
          style={{
            flex:       1,
            background: 'transparent',
            border:     'none',
            outline:    'none',
            color:      '#0B0B0B',
            fontSize:   14,
            padding:    '11px 16px',
            minWidth:   0,
          }}
        />
        <button
          onClick={handleSend}
          disabled={!canSend}
          aria-label={t('chat.send')}
          style={{
            flexShrink:     0,
            background:     'transparent',
            border:         'none',
            color:          canSend ? '#0B0B0B' : '#9A9A9A',
            width:          44,
            height:         42,
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
            cursor:         canSend ? 'pointer' : 'default',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M22 2L15 22 11 13 2 9l20-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function DesktopMessageRow({ msg }: { msg: ChatMessage }) {
  const { t } = useTranslation('gameplay');
  if (msg.kind === 'system') {
    return (
      <div
        className="font-sans"
        style={{
          textAlign:     'center',
          fontSize:      10,
          fontWeight:    700,
          letterSpacing: '0.8px',
          textTransform: 'uppercase',
          color:         DT.color.textMuted,
          padding:       '2px 0',
        }}
      >
        {msg.body}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        {/* Player Identity V1: sender's flag (humans with a country only). */}
        {!msg.sender_is_bot && msg.sender_display && (
          <CountryFlag countryCode={msg.sender_country_code} width={14} />
        )}
        <span
          className="font-sans"
          style={{ fontSize: 10, fontWeight: 700, color: DT.color.textMuted }}
        >
          {msg.sender_display ?? t('chat.unknownSender')}
        </span>
      </span>
      <span
        className="font-sans"
        style={{ fontSize: 12, color: DT.color.textPrimary, wordBreak: 'break-word', lineHeight: 1.4 }}
      >
        {msg.body}
      </span>
    </div>
  );
}
